import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import ReimaginedArtworkDetailsModal, {
  ReimaginedArtworkExpandedView,
} from './ReimaginedArtworkDetailsModal';
import { getTransformedArtworksByFeature, checkExistingActivity, checkTutorialById } from '../utils/activityService';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { formatActivityDate, toJSDate } from '../utils/date';
import { artStyles } from '../data/artStyles';
import { resolveImageUrl, getBackendBaseUrl } from '../utils/urlResolver';

const ReimaginedArtworkCard = ({
  originalArtworkUrl,
  originalArtworkTitle,
  onTutorialClick,
  onCardClick,
  onDeleteActivity
}) => {
  const navigate = useNavigate();
  const [transformedActivities, setTransformedActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [hasTutorial, setHasTutorial] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showExpandedView, setShowExpandedView] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (originalArtworkUrl && user) {
      fetchTransformedActivities();
    }
  }, [originalArtworkUrl, user]);

  const currentActivity = transformedActivities && transformedActivities.length > 0 && currentIndex >= 0 && currentIndex < transformedActivities.length
    ? transformedActivities[currentIndex]
    : null;

  const mergedStyle = (() => {
    if (!currentActivity) return {};
    const s = currentActivity.styleInfo || {};
    if (!s.id) return s;
    const base = (artStyles || []).filter(a => a != null).find(a => a.id === s.id) || {};
    return { ...base, ...s };
  })();

  const [expandedFeedback, setExpandedFeedback] = useState({});

  const { impactItem, suggestionItem } = useMemo(() => {
    if (!currentActivity || !currentActivity.metadata) return [];

    const meta = currentActivity.metadata;
    let source =
      meta.artmosphereFeedback ||
      (meta.transformations &&
        meta.transformations.length > 0 &&
        meta.transformations[meta.transformations.length - 1]?.artmosphereFeedback);

    if (!source) return [];

    const rawArray = Array.isArray(source) ? source : [source];

    const items = rawArray
      .map((it) => ({
        title: (it.title || it.label || '').trim(),
        text: (it.text || it.appreciation || '').trim(),
        suggestion: (it.suggestion || '').trim(),
      }))
      // Remove "Intro" section entirely
      .filter((it) => (it.title || '').toLowerCase() !== 'intro');
    
    const findByTitle = (keyword) =>
      items.find((it) => (it.title || '').toLowerCase().includes(keyword));

    return {
      impactItem: findByTitle('impact'),
      suggestionItem: findByTitle('suggest'),
    };
  }, [currentActivity]);

  const modalFeedbackItems = useMemo(() => {
    const items = [];
    if (impactItem?.text) {
      items.push({ title: 'Impact of Your Change', text: impactItem.text });
    }
    if (suggestionItem?.text || suggestionItem?.suggestion) {
      items.push({
        title: "Mentor's Suggestion",
        text: suggestionItem.text || '',
        suggestion: suggestionItem.suggestion || '',
      });
    }
    if (items.length > 0) return items;
    const meta = currentActivity?.metadata?.artmosphereFeedback;
    return Array.isArray(meta) ? meta : [];
  }, [impactItem, suggestionItem, currentActivity]);

  useEffect(() => {
    if (currentActivity && user) {
      checkTutorialExists();
    }
  }, [currentActivity, user]);

  useEffect(() => {
    console.log('🎯 hasTutorial state changed:', hasTutorial, 'for style:', currentActivity?.styleInfo?.name);
  }, [hasTutorial, currentActivity?.styleInfo?.name]);

  useEffect(() => {
    console.log('🎨 Expanded state changed:', expanded);
  }, [expanded]);

  // Ensure any outer dashboard modal is closed while this modal/expanded view is open
  useEffect(() => {
    if (showDetailsModal || expanded) {
      try {
        window.dispatchEvent(new Event('close-global-details-modal'));
      } catch (err) {}
    }
  }, [showDetailsModal, expanded]);

  const fetchTransformedActivities = async () => {
    if (!originalArtworkUrl || !user) return;
    
    setLoading(true);
    try {
      // Fetch all reimagine activities for this artwork (not filtered by feature)
      // This ensures we get old activities regardless of their feature value
      let snapshot;
      try {
        // Try with orderBy first (requires index)
        const activitiesQuery = query(
          collection(db, 'userActivities'),
          where('userId', '==', user.uid),
          where('originalImageUrl', '==', originalArtworkUrl),
          where('type', '==', 'reimagine'),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(activitiesQuery);
      } catch (orderError) {
        // If orderBy fails (missing index), try without it
        console.warn('⚠️ OrderBy query failed, fetching without order:', orderError);
        const activitiesQuery = query(
          collection(db, 'userActivities'),
          where('userId', '==', user.uid),
          where('originalImageUrl', '==', originalArtworkUrl),
          where('type', '==', 'reimagine')
        );
        snapshot = await getDocs(activitiesQuery);
      }
      const allActivities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter to only include activities that should show in ReimaginedArtworkCard:
      // - feature: 'reimagine-artwork'
      // - feature: 'reimagine'
      // - no feature (old activities)
      // Exclude: 'ways-of-seeing-journey', 'reimagine-with-feedback'
      const filteredActivities = allActivities.filter(activity => {
        const feature = activity.metadata?.feature;
        return (
          feature === 'reimagine-artwork' ||
          feature === 'reimagine' ||
          !feature || // Include old activities with no feature
          feature === undefined
        ) && (
          feature !== 'ways-of-seeing-journey' &&
          feature !== 'reimagine-with-feedback'
        );
      });
      
      // Sort by createdAt descending (client-side, in case orderBy query failed)
      filteredActivities.sort((a, b) => {
        const aTime = toJSDate(a.createdAt)?.getTime() || 0;
        const bTime = toJSDate(b.createdAt)?.getTime() || 0;
        return bTime - aTime;
      });
      
      setTransformedActivities(filteredActivities);
      setCurrentIndex(0);
      console.log('🎨 Fetched reimagine activities:', {
        originalArtworkUrl: originalArtworkUrl,
        totalActivities: allActivities.length,
        filteredActivities: filteredActivities.length,
        allFeatures: allActivities.map(a => ({
          id: a.id,
          title: a.title,
          feature: a.metadata?.feature || 'none',
          originalImageUrl: a.originalImageUrl
        })),
        filteredFeatures: filteredActivities.map(a => ({
          id: a.id,
          title: a.title,
          feature: a.metadata?.feature || 'none'
        }))
      });
    } catch (error) {
      console.error('Error fetching transformed activities:', error);
      // Fallback: try the old method if the query fails
      try {
        const [artworkActivities, reimagineActivities] = await Promise.all([
          getTransformedArtworksByFeature(originalArtworkUrl, user.uid, 'reimagine-artwork'),
          getTransformedArtworksByFeature(originalArtworkUrl, user.uid, 'reimagine')
        ]);
        const allActivities = [...artworkActivities, ...reimagineActivities];
        const uniqueActivities = allActivities.filter((activity, index, self) => 
          index === self.findIndex(a => a.id === activity.id)
        );
        uniqueActivities.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        setTransformedActivities(uniqueActivities);
        setCurrentIndex(0);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate tutorial ID (same as in activityService)
  const generateTutorialId = (styleId, artworkId) => {
    return `tutorial_${styleId}_${artworkId}`;
  };

  const checkTutorialExists = async () => {
    if (!currentActivity || !user) {
      console.log('❌ Cannot check tutorial: missing currentActivity or user');
      setHasTutorial(false);
      return;
    }
    
    // Check if we have the required data
    if (!currentActivity.styleInfo?.id || !currentActivity.id) {
      console.log('❌ Cannot check tutorial: missing styleId or activityId', {
        hasStyleId: !!currentActivity.styleInfo?.id,
        hasActivityId: !!currentActivity.id,
        styleInfo: currentActivity.styleInfo
      });
      setHasTutorial(false);
      return;
    }
    
    try {
    if (!currentActivity) {
      console.log('❌ Cannot check tutorial: currentActivity is undefined');
      setHasTutorial(false);
      return;
    }
    
    console.log('🔍 Checking tutorial existence for:', {
      style: currentActivity.styleInfo?.name,
      styleId: currentActivity.styleInfo?.id,
        activityId: currentActivity.id,
      userId: user.uid
    });
    
      // Use checkTutorialById to check for existing tutorial
      const existingTutorial = await checkTutorialById(
        currentActivity.styleInfo?.id,
        currentActivity.id
      );
      
      const hasTutorial = !!existingTutorial;
      console.log('🔍 Tutorial existence check result:', hasTutorial, existingTutorial ? `(ID: ${existingTutorial.id})` : '');
      
      setHasTutorial(hasTutorial);
      
      if (existingTutorial) {
        console.log('✅ Found existing tutorial:', {
          id: existingTutorial.id,
          tutorialId: existingTutorial.tutorialId,
          styleInfo: existingTutorial.styleInfo
        });
      }
    } catch (error) {
      console.error('❌ Error checking tutorial existence:', error);
      setHasTutorial(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => {
      const newIndex = prev > 0 ? prev - 1 : transformedActivities.length - 1;
      // Check tutorial existence for the new activity after state update
      setTimeout(() => {
        if (transformedActivities[newIndex]) {
          checkTutorialExists();
        }
      }, 0);
      return newIndex;
    });
  };

  const handleNext = () => {
    setCurrentIndex(prev => {
      const newIndex = prev < transformedActivities.length - 1 ? prev + 1 : 0;
      // Check tutorial existence for the new activity after state update
      setTimeout(() => {
        if (transformedActivities[newIndex]) {
          checkTutorialExists();
        }
      }, 0);
      return newIndex;
    });
  };

  const getExpandedImage = (activity) => {
    if (!activity) return '';
    
    console.log('🔍 getExpandedImage called with activity:', activity);
    
    // Check all possible image URL fields
    const possibleUrls = [
      activity?.transformedImageUrl,
      activity?.imageUrl,
      activity?.result?.imageUrl,
      activity?.metadata?.transformedImageUrl,
      activity?.metadata?.imageUrl,
      activity?.data?.imageUrl,
      activity?.transformedImage,
      activity?.resultImageUrl
    ];
    
    // Check for journey steps first
    if (activity.journey && activity.journey.length > 0) {
      const lastStep = activity.journey[activity.journey.length - 1];
      if (lastStep.transformedImageUrl) {
        console.log('🎨 Found image in journey step:', lastStep.transformedImageUrl);
        return getFullImageUrl(lastStep.transformedImageUrl);
      }
    }
    
    // Check main transformed image
    if (activity.transformedImageUrl) {
      console.log('🎨 Found main transformed image:', activity.transformedImageUrl);
      return getFullImageUrl(activity.transformedImageUrl);
    }
    
    // Check other possible fields
    for (const url of possibleUrls) {
      if (url && typeof url === 'string' && url.length > 0) {
        console.log('🎨 Found image in other field:', url);
        return getFullImageUrl(url);
      }
    }
    
    console.log('❌ No image URL found in activity:', {
      transformedImageUrl: activity.transformedImageUrl,
      imageUrl: activity.imageUrl,
      hasJourney: !!activity.journey,
      journeyLength: activity.journey?.length || 0
    });
    
    return '';
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    
    // Check if it's a BFL URL that needs proxying
    if (url.includes('bfl.ai') || url.includes('delivery-eu4.bfl.ai')) {
      const backendBase = getBackendBaseUrl();
      return `${backendBase}/proxy-image?url=${encodeURIComponent(url)}`;
    }
    
    // Use centralized URL resolver to handle localhost:3001 URLs
    return resolveImageUrl(url);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-2xl p-4 animate-pulse">
        <div className="h-32 bg-white/30 rounded-lg"></div>
      </div>
    );
  }

  if (!currentActivity) {
    return null;
  }

  const toggleFeedbackExpansion = (index) => {
    setExpandedFeedback((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div 
      className="bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
      onClick={() => {
        // Open the same details modal for both card click and view details button
        try { window.dispatchEvent(new Event('close-global-details-modal')); } catch (err) {}
        setShowDetailsModal(true);
      }}
    >
      {/* Subtle background removed (no emojis) */}
      <div className="absolute inset-0 opacity-10">
        
      </div>

      {/* Strip Label */}
      <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">
        Re-imagined Artwork
      </div>

      {/* Transformed Artwork Only */}
      <div className="relative mb-2 z-10 mt-3">
        <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
          <img 
            src={getExpandedImage(currentActivity)} 
            alt={`${currentActivity?.styleInfo?.name || 'Transformed'} artwork`} 
            className="w-full h-full object-cover"
            onLoad={() => {
              console.log('✅ Image loaded successfully:', getExpandedImage(currentActivity));
            }}
            onError={(e) => {
              console.error('❌ Failed to load image:', getExpandedImage(currentActivity));
              console.error('❌ Current activity:', currentActivity);
              e.target.style.display = 'none';
              // Show fallback content
              const fallback = e.target.parentElement.querySelector('.image-fallback');
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          {/* Fallback when image fails to load */}
          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
            <div className="text-center">
              <p className="text-sm text-orange-600 font-medium">
                Re-imagined Artwork
              </p>
            </div>
          </div>
          
          {/* Navigation arrows for multiple transformations */}
          {transformedActivities.length > 1 && (
            <div className="absolute top-1 right-1 flex gap-1 z-30">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                className="bg-orange-500/90 hover:bg-orange-600 text-white p-1 rounded-full transition-colors shadow-md"
                title="Previous artwork"
              >
                <FaChevronLeft size={10} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="bg-orange-500/90 hover:bg-orange-600 text-white p-1 rounded-full transition-colors shadow-md"
                title="Next artwork"
              >
                <FaChevronRight size={10} />
              </button>
            </div>
          )}
          
          {/* Delete button on bottom right of image thumbnail */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Delete this awesome creation? This cannot be undone!')) {
                if (onDeleteActivity && currentActivity) {
                  onDeleteActivity(currentActivity.id);
                  // Remove from local state
                  setTransformedActivities(prev => {
                    const newActivities = prev.filter(activity => activity.id !== currentActivity.id);
                    // Adjust current index if needed
                    if (currentIndex >= newActivities.length && newActivities.length > 0) {
                      setCurrentIndex(newActivities.length - 1);
                    } else if (newActivities.length === 0) {
                      setCurrentIndex(0);
                    }
                    return newActivities;
                  });
                }
              }
            }}
            className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-30"
            title="Delete this creation"
          >
            <FaTrash size={8} />
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-1 relative z-10">
        <div className="text-center flex-1">
          <p className="text-xs text-orange-700 font-medium">
            {formatActivityDate(currentActivity)}
          </p>
        </div>
        
        {/* Page indicator for multiple transformations */}
        {transformedActivities.length > 1 && (
          <div className="text-xs text-orange-600 font-medium">
            {currentIndex + 1}/{transformedActivities.length}
          </div>
        )}
      </div>

      {/* View Details Button */}
      <div className="relative z-10 mt-1">
        <button 
          onClick={(e) => {
            e.stopPropagation();
            try { window.dispatchEvent(new Event('close-global-details-modal')); } catch (err) {}
            setShowDetailsModal(true);
          }}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
          style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
        >
          View details
        </button>
      </div>

      <ReimaginedArtworkDetailsModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        originalArtworkUrl={resolveImageUrl(originalArtworkUrl)}
        transformedImageUrl={getExpandedImage(currentActivity)}
        activity={{
          ...currentActivity,
          metadata: {
            ...currentActivity?.metadata,
            prompt:
              currentActivity?.metadata?.prompt ||
              currentActivity?.metadata?.transformations?.[0]?.prompt,
            userFeedback:
              currentActivity?.metadata?.userFeedback ||
              currentActivity?.metadata?.transformations?.[0]?.userFeedback,
            artmosphereFeedback: modalFeedbackItems,
          },
        }}
        expandedFeedback={expandedFeedback}
        onToggleFeedback={toggleFeedbackExpansion}
        onExpandImage={() => {
          setShowDetailsModal(false);
          setExpanded(true);
        }}
        showNavigation={transformedActivities.length > 1}
        currentIndex={currentIndex}
        totalCount={transformedActivities.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />

      <ReimaginedArtworkExpandedView
        open={expanded}
        imageUrl={getExpandedImage(currentActivity)}
        onClose={() => {
          setExpanded(false);
          setShowDetailsModal(true);
        }}
      />
    </div>
  );
};

export default ReimaginedArtworkCard;
